/**
 * PaceModule — A3 module 2: run-rate vs annual target, YTD pace, and a linear
 * year-end projection with the gap to goal, for the money-flow metrics (registry
 * pick, CD-4). Composition only (CD-7): the pace maths live in paceModel. The method
 * is labelled on every card (linear run-rate, not seasonalised), unserved months are
 * named gaps rather than interpolated, and "ahead/behind" respects each metric's
 * direction so a lower-is-better cost reads correctly.
 */

import React from 'react'
import InfoBanner from '../components/InfoBanner'
import { periodHeading, scaleForDisplay } from '../components/kit/cellFormat'
import { formatCurrency, formatNumber, formatPercentage } from '../utils/formatters'
import { buildPace, pickPaceMetrics, type PaceModel } from '../components/kit/paceModel'
import { type Scope } from '../hooks/useScope'
import type { CatalogueResponse, MetricFormat, ResolveResponse } from '../types/metrics'

function formatPace(raw: number | null, format: MetricFormat): string {
  if (raw === null) return '—'
  const scaled = scaleForDisplay(raw, format)
  if (format === 'gbp_k') return `${formatCurrency(scaled, '£', Math.abs(scaled) < 100 ? 1 : 0)}k`
  if (format === 'pct') return formatPercentage(scaled)
  return formatNumber(scaled, 0)
}

function GapChip({ pace }: { pace: PaceModel }): React.JSX.Element {
  if (pace.ahead === null) {
    return <span className="font-mono text-[11px] text-mut2">gap unavailable</span>
  }
  const tone = pace.ahead ? 'text-good' : 'text-bad'
  const word = pace.ahead ? 'ahead of' : 'behind'
  const signed = `${(pace.gap as number) >= 0 ? '+' : '−'}${formatPace(Math.abs(pace.gap as number), pace.format)}`
  return (
    <span className={`text-[12.5px] font-semibold ${tone}`} data-testid="pace-gap" data-ahead={pace.ahead}>
      {signed} · {word} target
    </span>
  )
}

function PaceCard({ pace }: { pace: PaceModel }): React.JSX.Element {
  return (
    <div className="border border-line rounded-xl bg-panel px-4 py-3.5 space-y-2" data-testid={`pace-card-${pace.metricId}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13.5px] font-bold text-ink">{pace.title}</span>
        <span className="font-mono text-[9px] uppercase tracking-[.08em] text-mut2">{pace.basis === 'sum' ? 'flow · YTD' : 'rate · mean'}</span>
      </div>
      {pace.projection === null ? (
        <p className="text-[12.5px] text-mut2">Not served in this window — no pace basis.</p>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className="text-[22px] font-bold tracking-[-.01em] text-ink">{formatPace(pace.projection, pace.format)}</span>
            <span className="text-[11px] text-mut2">annualised run-rate</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] text-mut">
            <span>YTD {formatPace(pace.ytdActual, pace.format)}</span>
            <span>annual target {formatPace(pace.annualTarget, pace.format)}</span>
          </div>
          <GapChip pace={pace} />
        </>
      )}
      {pace.monthsMissing.length > 0 && (
        <p className="text-[11px] text-amber" data-testid="pace-gaps">
          {pace.monthsMissing.length} unserved month{pace.monthsMissing.length === 1 ? '' : 's'} excluded from the basis: {pace.monthsMissing.map(periodHeading).join(', ')}
        </p>
      )}
      <p className="font-mono text-[9.5px] leading-[1.4] text-mut2">{pace.method}</p>
    </div>
  )
}

export default function PaceModule({
  scope,
  resolve,
  catalogue,
}: {
  scope: Scope
  resolve: ResolveResponse
  catalogue: CatalogueResponse
}): React.JSX.Element {
  // Pace is a monthly, calendar-year concept (run-rate × 12); refuse to annualise
  // weekly data — a weekly ×12 would be ~4× wrong and mislabel weeks as months.
  if (scope.cadence !== 'monthly') {
    return (
      <InfoBanner
        variant="info"
        message="Pace is a monthly, calendar-year measure — switch to monthly cadence to see run-rate vs annual target."
      />
    )
  }
  const metrics = pickPaceMetrics(catalogue, scope.cadence)
  if (metrics.length === 0) {
    return (
      <InfoBanner
        variant="info"
        message={`No money-flow metrics served for ${scope.periodFrom} → ${scope.periodTo}.`}
      />
    )
  }
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="pace-module">
      {metrics.map((metric) => (
        <PaceCard key={metric.id} pace={buildPace(resolve, metric, scope.site)} />
      ))}
    </div>
  )
}
